/**
 * RBAC Service
 *
 * Roles & permissions management API calls (/api/rbac/**).
 * Functions return the raw ApiResponse so callers can branch on `statusCode`
 * / `status` and surface `showMessage` from the backend.
 */

import { apiClient, type ApiResponse } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import type {
  MyPermissions,
  PermissionCatalogItem,
  MatrixModule,
  RoleMatrix,
  SaveRoleMatrixRequest,
  RbacRole,
  RbacRoleDetail,
  UserRoleAssignment,
  CreateRoleRequest,
  UpdateRoleRequest,
  DeleteRoleRequest,
  SetRolePermissionsRequest,
  AssignRoleRequest,
  RevokeRoleRequest,
} from '../types';

const { RBAC } = API_ENDPOINTS;

// ============================================
// Self-service — bootstrap the permission store
// ============================================

/** Current user's effective permissions: { userId, userType, permissions }. */
export async function getMyPermissions(): Promise<ApiResponse<MyPermissions>> {
  const response = await apiClient.get<ApiResponse<MyPermissions>>(RBAC.MY_PERMISSIONS);
  return response.data;
}

// ============================================
// Catalog & Roles
// ============================================

/** All permissions in the catalog (key, type, module, description). */
export async function getPermissionCatalog(): Promise<ApiResponse<PermissionCatalogItem[]>> {
  const response = await apiClient.get<ApiResponse<PermissionCatalogItem[]>>(RBAC.PERMISSIONS);
  return response.data;
}

/** Grouped, role-independent permission tree (module → section → permission). */
export async function getPermissionTree(): Promise<ApiResponse<MatrixModule[]>> {
  const response = await apiClient.get<ApiResponse<MatrixModule[]>>(RBAC.PERMISSION_TREE);
  return response.data;
}

/** Toggle matrix for a role: { role, hasParent, hasAdvancedRules, tree }. */
export async function getRoleMatrix(roleId: string): Promise<ApiResponse<RoleMatrix>> {
  const response = await apiClient.get<ApiResponse<RoleMatrix>>(RBAC.ROLE_MATRIX(roleId));
  return response.data;
}

/** Save the full ON-set for a role (replace semantics, optimistic version). */
export async function saveRoleMatrix(
  roleId: string,
  payload: SaveRoleMatrixRequest
): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ROLE_MATRIX(roleId), payload);
  return response.data;
}

/** Reset a SYSTEM role's permissions back to seed defaults. */
export async function resetSystemRole(roleId: string): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ROLE_RESET, { roleId });
  return response.data;
}

/** All roles of the org (id, code, name, isSystem, assigneeCount, version). */
export async function getRoles(): Promise<ApiResponse<RbacRole[]>> {
  const response = await apiClient.get<ApiResponse<RbacRole[]>>(RBAC.ROLES);
  return response.data;
}

/** Full detail of one role, including its granted permission keys. */
export async function getRoleById(roleId: string): Promise<ApiResponse<RbacRoleDetail>> {
  const response = await apiClient.get<ApiResponse<RbacRoleDetail>>(RBAC.ROLE_BY_ID(roleId));
  return response.data;
}

export async function createRole(
  payload: CreateRoleRequest
): Promise<ApiResponse<{ roleId: string }>> {
  const response = await apiClient.post<ApiResponse<{ roleId: string }>>(RBAC.ROLE_CREATE, payload);
  return response.data;
}

export async function updateRole(payload: UpdateRoleRequest): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ROLE_UPDATE, payload);
  return response.data;
}

export async function deleteRole(payload: DeleteRoleRequest): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ROLE_DELETE, payload);
  return response.data;
}

/** Replace the full permission set of a role (the "save permissions" action). */
export async function setRolePermissions(
  payload: SetRolePermissionsRequest
): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ROLE_PERMISSIONS, payload);
  return response.data;
}

// ============================================
// Assignments
// ============================================

/** Role assignments of one user. */
export async function getUserRoles(userId: string): Promise<ApiResponse<UserRoleAssignment[]>> {
  const response = await apiClient.get<ApiResponse<UserRoleAssignment[]>>(RBAC.USER_ROLES(userId));
  return response.data;
}

export async function assignRole(payload: AssignRoleRequest): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.ASSIGN, payload);
  return response.data;
}

export async function revokeRole(payload: RevokeRoleRequest): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post<ApiResponse<unknown>>(RBAC.REVOKE, payload);
  return response.data;
}

export const rbacService = {
  getMyPermissions,
  getPermissionCatalog,
  getPermissionTree,
  getRoleMatrix,
  saveRoleMatrix,
  resetSystemRole,
  getRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  setRolePermissions,
  getUserRoles,
  assignRole,
  revokeRole,
};
